/**
 * @file Web Gallery discovery component.
 */

import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from 'react-apollo';
import qs from 'query-string';

import Button from '../../../components/global/Button';
import { getErrorString } from '../../../util/normalize-errors';
import Alert from '../../../components/global/Alert';
import { isAlbum } from '../../../util/album';
import { isAlbumFolder } from '../../../util/album-folder';
import { sanitizeJoinCode } from '../../../util/join-code';
import InputGroup from '../../../components/global/InputGroup';
import PhoneAuthWithEmailFallback from '../../../components/auth/PhoneAuthWithEmailFallback';
import { prependAccountSlug } from '../../../util/account';
import ConsumerEmailAuthAllowed from '../../../components/auth/ConsumerEmailAuthAllowed';
import { AlbumFolderForWebGalleries } from '../graphql/queries/album-folder';
import { AlbumForWebGalleries } from '../graphql/queries/album';
import { JoinCodeByCodeForWebGalleryDiscovery } from '../graphql/queries/code';
import { trackAuthEvent } from '../utilities/analytics';
import {
  FAILED_TO_FETCH_ERROR_MESSAGE,
  FAILED_TO_FETCH_ERROR_REPLACEMENT,
} from '../../../constants/error-messages';
import useAuthenticated from '../../../hooks/useAuthenticated';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

const WebGalleryDiscovery = ({ history, location }) => {
  const client = useApolloClient();
  const authenticated = useAuthenticated();
  const { joinCode: joinCodeQuery = '' } = qs.parse(location?.search);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requireAuth, setRequireAuth] = useState(false);
  const [hierarchyItem, setHierarchyItem] = useState(null);
  const [joinCodeInput, setJoinCodeInput] = useState(
    sanitizeJoinCode(joinCodeQuery),
  );

  const onInputChange = ({ target }) => {
    const { value } = target;
    setJoinCodeInput(sanitizeJoinCode(value));
  };

  const getWebGalleryUrl = (currentHierarchyItem = hierarchyItem) => {
    if (!currentHierarchyItem) {
      return '';
    }

    return isAlbumFolder(currentHierarchyItem)
      ? prependAccountSlug(
          `/galleries/view/folder/${currentHierarchyItem.id}`,
          currentHierarchyItem,
        )
      : prependAccountSlug(
          `/galleries/view/album/${currentHierarchyItem.id}`,
          currentHierarchyItem,
        );
  };

  const onGalleryForward = async (currentHierarchyItem = hierarchyItem) => {
    if (currentHierarchyItem.is_web_gallery && isAlbum(currentHierarchyItem)) {
      // Prefetch album
      await client.query({
        query: AlbumForWebGalleries,
        variables: {
          id: currentHierarchyItem.id,
          ignore_watermark: true,
          limit: 25,
          offset: 0,
          for_web_gallery: true,
        },
      });

      history.push(getWebGalleryUrl(currentHierarchyItem));
    }

    if (
      currentHierarchyItem.is_web_gallery &&
      isAlbumFolder(currentHierarchyItem)
    ) {
      // Prefetch album folder
      await client.query({
        query: AlbumFolderForWebGalleries,
        variables: {
          id: currentHierarchyItem.id,
        },
      });

      history.push(getWebGalleryUrl(currentHierarchyItem));
    }
  };

  const onFormSubmit = async (event = {}) => {
    event?.preventDefault?.();

    setLoading(true);
    setError('');

    if (!joinCodeInput) {
      setLoading(false);
      setError('Please enter a code');

      return;
    }

    try {
      const {
        data: { joinCodeByCode },
      } = await client.query({
        query: JoinCodeByCodeForWebGalleryDiscovery,
        variables: {
          code: joinCodeInput,
        },
        fetchPolicy: 'network-only',
      });

      const foundHierarchyItem =
        joinCodeByCode?.album ?? joinCodeByCode?.album_folder;

      // If nothing is found or the response object is not a web gallery
      // present the user with an error message and halt
      if (!foundHierarchyItem?.is_web_gallery) {
        setLoading(false);
        setError(
          'The Web Gallery you requested could not be found. Please make sure you have the right code and try again.',
        );

        return;
      }

      setHierarchyItem(foundHierarchyItem);
      setLoading(false);
      setError('');

      if (foundHierarchyItem?.is_private_web_gallery && !authenticated) {
        setRequireAuth(true);
      } else {
        await onGalleryForward(foundHierarchyItem);
      }
    } catch (err) {
      let message = getErrorString(err);
      if (message === FAILED_TO_FETCH_ERROR_MESSAGE) {
        message = FAILED_TO_FETCH_ERROR_REPLACEMENT;
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (joinCodeQuery) {
      onFormSubmit();
    }
  }, [joinCodeQuery]);

  return !requireAuth ? (
    <Fragment>
      <form
        method="post"
        onSubmit={onFormSubmit}
        data-testid="web-gallery-discovery-form"
      >
        <p className="h4 mb-2">
          Enter your join code to view your photo gallery
        </p>

        {error && (
          <Alert type="danger" data-testid="web-gallery-discovery-error">
            {error}
          </Alert>
        )}

        <div className="d-flex align-items-center">
          <InputGroup
            addon="#"
            id="gallery-code"
            name="joinCodeInput"
            label="Enter a gallery code"
            labelSrOnly
            placeholder="i.e. AwesomeGallery19"
            inputClassName="d-inline"
            value={joinCodeInput}
            onChange={onInputChange}
            wrapperClassName="flex-auto m-0 mr-1"
            data-testid="web-gallery-discovery-join-code-input"
            required
          />

          <Button
            className="m-0 flex-none min-w-0"
            type="submit"
            disabled={loading}
            data-testid="web-gallery-discovery-submit"
          >
            {loading ? 'Loading...' : 'Go'}
          </Button>
        </div>
      </form>
    </Fragment>
  ) : (
    <ConsumerEmailAuthAllowed id={hierarchyItem?.id}>
      {allowed => (
        <PhoneAuthWithEmailFallback
          onLogin={authType => {
            trackAuthEvent(authType);
            onGalleryForward();
          }}
          emailVerificationRedirectUrl={getWebGalleryUrl()}
          defaultEmailStep="LOGIN"
          enableEmail={allowed}
        />
      )}
    </ConsumerEmailAuthAllowed>
  );
};

WebGalleryDiscovery.propTypes = propTypes;

export default WebGalleryDiscovery;
